import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Users,
  UserPlus,
  Edit,
  Trash2,
  Key,
  Mail,
  Phone,
  X,
  Eye,
  EyeOff,
  Search,
  Filter,
  RefreshCw,
  Shield,
  Briefcase,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Globe,
  Plane,
  Sparkles,
  Database,
  AlertTriangle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  "México", "Colombia", "Argentina", "Perú", "Chile", "Ecuador", "Venezuela", 
  "Guatemala", "Honduras", "El Salvador", "Nicaragua", "Costa Rica", "Panamá", 
  "República Dominicana", "Brasil", "España", "Estados Unidos", "Otro"
];

// Create Vendor Trial Modal
const CreateVendorTrialModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "México"
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Nombre requerido";
    if (!formData.email.trim()) newErrors.email = "Email requerido";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Email inválido";
    if (!formData.phone.trim()) newErrors.phone = "Teléfono requerido";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/auth/vendor-trial-account`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Vendedor en prueba creado: ${formData.name}`, {
        description: `Credenciales enviadas a ${formData.email}`
      });
      
      setFormData({ name: "", email: "", phone: "", country: "México" });
      onCreated && onCreated(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear vendedor");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Crear Vendedor Prueba</h2>
                <p className="text-purple-100 text-sm">Cuenta demo por 7 días</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" /> Nombre completo *
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nombre del vendedor"
                className={errors.name ? "border-red-500" : ""}
                data-testid="vendor-trial-name"
              />
              {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email *
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="vendedor@email.com"
                className={errors.email ? "border-red-500" : ""}
                data-testid="vendor-trial-email"
              />
              {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Teléfono *
                </Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+52 55 1234 5678"
                  className={errors.phone ? "border-red-500" : ""}
                  data-testid="vendor-trial-phone"
                />
                {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" /> País
                </Label>
                <Select value={formData.country} onValueChange={(v) => setFormData({ ...formData, country: v })}>
                  <SelectTrigger data-testid="vendor-trial-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-purple-700">
                <strong>Nota:</strong> El vendedor recibirá un email con sus credenciales y podrá explorar el sistema en modo demo por 7 días.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={loading} data-testid="create-vendor-trial-submit">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Crear Prueba
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Change Password Modal for Staff
const ChangePasswordModal = ({ staff, isOpen, onClose }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let pwd = "";
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(pwd);
    setShowPassword(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/users/${staff.id}/change-password`, { 
        new_password: password || null,
        send_email: sendEmail 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResult({
        success: true,
        password: response.data.new_password,
        email_sent: response.data.email_sent
      });
      
      toast.success(`Contraseña actualizada para ${staff.name}`, {
        description: sendEmail ? "Email enviado con las credenciales" : "Sin notificación por email"
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar contraseña");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword("");
    setResult(null);
    setSendEmail(true);
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Contraseña copiada al portapapeles");
  };

  if (!isOpen || !staff) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
            <button 
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Key className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Cambiar Contraseña</h2>
                <p className="text-amber-100 text-sm">{staff.name} • {staff.email}</p>
              </div>
            </div>
          </div>

          {result ? (
            // Success Result View
            <div className="p-6 space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="font-semibold text-emerald-800 mb-1">¡Contraseña Actualizada!</h3>
                <p className="text-sm text-emerald-600">
                  {result.email_sent 
                    ? "Se envió un email al usuario con la nueva contraseña"
                    : "Contraseña actualizada sin notificación por email"}
                </p>
              </div>

              <div className="bg-navy-50 rounded-xl p-4">
                <p className="text-xs text-navy-500 mb-2">Nueva contraseña:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white px-4 py-3 rounded-lg font-mono text-lg text-navy-900 border border-navy-200 text-center">
                    {result.password}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(result.password)}
                    className="shrink-0"
                  >
                    Copiar
                  </Button>
                </div>
              </div>

              <Button onClick={handleClose} className="w-full bg-navy-800 hover:bg-navy-900">
                Cerrar
              </Button>
            </div>
          ) : (
            // Form View
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-navy-700 font-medium">Nueva contraseña</Label>
                  <button type="button" onClick={generatePassword} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                    Generar automática
                  </button>
                </div>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Dejar vacío para generar automática"
                    className="pl-10 pr-10 h-11"
                    data-testid="change-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-navy-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-navy-500">Mínimo 6 caracteres o dejar vacío para generar una automáticamente</p>
              </div>

              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Enviar por email</p>
                    <p className="text-xs text-blue-600">Notificar al usuario la nueva contraseña</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={sendEmail} 
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={handleClose} className="flex-1" disabled={loading}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" disabled={loading} data-testid="change-password-submit">
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Cambiar Contraseña"}
                </Button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Create Staff Modal
const CreateStaffModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "vendedor",
    vendor_level: "basico",
    travel_website: ""
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let pwd = "";
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password: pwd });
    setShowPassword(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Complete todos los campos requeridos");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/staff`, formData);
      toast.success("Vendedor creado exitosamente");
      onCreated(); // Just notify to refresh the list
      onClose();
      setFormData({ name: "", email: "", phone: "", password: "", role: "vendedor", travel_website: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4"
      >
        <div className="flex items-center justify-between p-6 border-b border-navy-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-navy-900">Nuevo Staff</h2>
              <p className="text-sm text-navy-500">Crear nuevo Vendedor</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-navy-50 rounded-lg">
            <X className="w-5 h-5 text-navy-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Rol *</Label>
            <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
              <SelectTrigger data-testid="staff-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vendedor">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                    Agente Vendedor
                  </div>
                </SelectItem>
                {/* REMOVED: oficial role - eliminated and merged into admin */}
              </SelectContent>
            </Select>
          </div>

          {/* Vendor Level - Only show for vendedor role */}
          {formData.role === "vendedor" && (
            <div className="space-y-2">
              <Label className="text-navy-700 font-medium">Nivel de Vendedor *</Label>
              <Select value={formData.vendor_level} onValueChange={(v) => setFormData({ ...formData, vendor_level: v })}>
                <SelectTrigger data-testid="vendor-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basico">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-500">⭐</span>
                      Básico - Solo Premium y VIP
                    </div>
                  </SelectItem>
                  <SelectItem value="profesional">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-500">⭐⭐</span>
                      Profesional - Todos los planes
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-navy-500">
                {formData.vendor_level === "basico" 
                  ? "Puede vender: Premium y VIP" 
                  : "Puede vender: Standard, Premium y VIP"}
              </p>
            </div>
          )}

          {/* Travel Website - Only show for vendedor role */}
          {formData.role === "vendedor" && (
            <div className="space-y-2">
              <Label className="text-navy-700 font-medium flex items-center gap-2">
                <Plane className="w-4 h-4 text-sky-500" />
                Sitio Web de Viajes (Opcional)
              </Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                <Input
                  type="url"
                  value={formData.travel_website}
                  onChange={(e) => setFormData({ ...formData, travel_website: e.target.value })}
                  placeholder="https://www.ejemplo.com"
                  className="pl-10"
                  data-testid="travel-website-input"
                />
              </div>
              <p className="text-xs text-navy-500">
                Se mostrará en la promoción de viajes para los clientes de este vendedor.
                Si no se especifica, se usará www.disneysuperfacil.com
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Nombre completo *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre del staff"
              data-testid="staff-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@dividelo.com"
                className="pl-10"
                data-testid="staff-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Teléfono</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+52 55 1234 5678"
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-navy-700 font-medium">Contraseña *</Label>
              <button type="button" onClick={generatePassword} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">
                Generar
              </button>
            </div>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Contraseña"
                className="pl-10 pr-10"
                data-testid="staff-password-input"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2">
                {showPassword ? <EyeOff className="w-4 h-4 text-navy-400" /> : <Eye className="w-4 h-4 text-navy-400" />}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={loading} data-testid="create-staff-submit">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Crear Staff"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Edit Staff Modal
const EditStaffModal = ({ isOpen, staff, onClose, onUpdated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vendor_level: "basico",
    travel_website: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (staff) {
      setFormData({
        name: staff.name || "",
        email: staff.email || "",
        phone: staff.phone || "",
        vendor_level: staff.vendor_level || "basico",
        travel_website: staff.travel_website || ""
      });
    }
  }, [staff]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Nombre y email son requeridos");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/admin/staff/${staff.id}`, formData);
      toast.success("Staff actualizado exitosamente");
      onUpdated(); // Notify to refresh
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !staff) return null;

  const isVendedor = staff.role === "vendedor";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4"
      >
        <div className="flex items-center justify-between p-6 border-b border-navy-100">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              isVendedor ? "bg-blue-100" : "bg-purple-100"
            }`}>
              <Edit className={`w-5 h-5 ${isVendedor ? "text-blue-600" : "text-purple-600"}`} />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-navy-900">Editar Vendedor</h2>
              <p className="text-sm text-navy-500">Modificar datos del vendedor</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-navy-50 rounded-lg">
            <X className="w-5 h-5 text-navy-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Nombre completo *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre del staff"
              data-testid="edit-staff-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@dividelo.com"
                className="pl-10"
                data-testid="edit-staff-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-navy-700 font-medium">Teléfono</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+52 55 1234 5678"
                className="pl-10"
                data-testid="edit-staff-phone-input"
              />
            </div>
          </div>

          {/* Vendor Level - Only show for vendedor role */}
          {isVendedor && (
            <div className="space-y-2">
              <Label className="text-navy-700 font-medium">Nivel de Vendedor</Label>
              <Select value={formData.vendor_level} onValueChange={(v) => setFormData({ ...formData, vendor_level: v })}>
                <SelectTrigger data-testid="edit-vendor-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basico">
                    <div className="flex items-center gap-2">
                      <span className="text-amber-500">⭐</span>
                      Básico - Solo Premium y VIP
                    </div>
                  </SelectItem>
                  <SelectItem value="profesional">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-500">⭐⭐</span>
                      Profesional - Todos los planes
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Travel Website - Only show for vendedor role */}
          {isVendedor && (
            <div className="space-y-2">
              <Label className="text-navy-700 font-medium flex items-center gap-2">
                <Plane className="w-4 h-4 text-sky-500" />
                Sitio Web de Viajes
              </Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                <Input
                  type="url"
                  value={formData.travel_website}
                  onChange={(e) => setFormData({ ...formData, travel_website: e.target.value })}
                  placeholder="https://www.disneysuperfacil.com"
                  className="pl-10"
                  data-testid="edit-travel-website-input"
                />
              </div>
              <p className="text-xs text-navy-500">
                Se mostrará en la promoción Orlando + Miami para clientes de este vendedor
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" disabled={loading} data-testid="update-staff-submit">
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Guardar Cambios"}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Staff Row Component
const StaffRow = ({ staff, onEdit, onDelete, onToggleActive, onChangePassword }) => {
  const isVendedor = staff.role === "vendedor";
  const isActive = staff.is_active !== false;
  const vendorLevel = staff.vendor_level || "basico";
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white border rounded-xl p-4 hover:shadow-md transition-shadow ${!isActive ? "opacity-60" : ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
            isVendedor ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
          }`}>
            {staff.name?.charAt(0).toUpperCase() || "?"}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-navy-900">{staff.name}</h3>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                Vendedor
              </span>
              {isVendedor && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  vendorLevel === "profesional" 
                    ? "bg-emerald-100 text-emerald-700" 
                    : "bg-amber-100 text-amber-700"
                }`}>
                  {vendorLevel === "profesional" ? "⭐⭐ Profesional" : "⭐ Básico"}
                </span>
              )}
              {!isActive && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                  Inactivo
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-navy-500 mt-1">
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" /> {staff.email}
              </span>
              {staff.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {staff.phone}
                </span>
              )}
            </div>
            <p className="text-xs text-navy-400 mt-1">
              Clientes asignados: {staff.assigned_clients?.length || 0}
              {isVendedor && (
                <span className="ml-3">
                  Planes: {vendorLevel === "profesional" ? "Standard, Premium, VIP" : "Premium, VIP"}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-navy-500">Activo</span>
            <Switch
              checked={isActive}
              onCheckedChange={(v) => onToggleActive(staff.id, v)}
            />
          </div>
          <Button variant="ghost" size="sm" onClick={() => onChangePassword(staff)} title="Cambiar contraseña" data-testid={`change-password-${staff.id}`}>
            <Key className="w-4 h-4 text-amber-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(staff)}>
            <Edit className="w-4 h-4 text-blue-600" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(staff)}>
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Component
const AdminStaff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateTrialModal, setShowCreateTrialModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [passwordStaff, setPasswordStaff] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/staff`);
      setStaff(response.data);
    } catch (error) {
      toast.error("Error al cargar staff");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (staffId, isActive) => {
    try {
      await axios.put(`${API}/admin/staff/${staffId}`, { is_active: isActive });
      // Refetch data to ensure consistency (avoid UI freeze from state mutation)
      await fetchStaff();
      toast.success(isActive ? "Staff activado" : "Staff desactivado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setShowEditModal(true);
  };

  const handleEditUpdated = () => {
    fetchStaff(); // Refresh list to ensure consistency
  };

  const handleDelete = async (staffMember) => {
    if (!window.confirm(`¿Eliminar a ${staffMember.name}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/staff/${staffMember.id}`);
      // Refetch data to ensure consistency (avoid UI freeze from state mutation)
      await fetchStaff();
      toast.success("Staff eliminado");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleMigrateTo6Steps = async () => {
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/admin/migrate-to-6-steps`);
      const { migrated, already_migrated, total_cases, errors } = response.data;
      
      toast.success(`Migración completada`, {
        description: `${migrated} casos actualizados, ${already_migrated} ya estaban en 5 pasos`
      });
      
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} errores durante la migración`);
      }
      
      setShowMigrationModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error durante la migración");
    } finally {
      setMigrating(false);
    }
  };

  const filteredStaff = staff.filter(s => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!s.name?.toLowerCase().includes(query) && !s.email?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (filterRole !== "all" && s.role !== filterRole) return false;
    return true;
  });

  const vendedores = staff.filter(s => s.role === "vendedor");
  const admins = staff.filter(s => s.role === "admin" || s.role === "legal" || s.role === "asesor");

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to="/admin" className="p-2 hover:bg-navy-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-navy-500" />
              </Link>
              <h1 className="font-heading text-3xl font-bold text-navy-900">Equipo de Trabajo</h1>
            </div>
            <p className="text-navy-500 ml-12">Gestiona el equipo de vendedores</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowMigrationModal(true)} variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Database className="w-4 h-4 mr-2" />
              Migrar a 6 Pasos
            </Button>
            <Button onClick={fetchStaff} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button onClick={() => setShowCreateTrialModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-vendor-trial-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Vendedor Prueba
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-staff-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Nuevo Staff
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <p className="text-sm text-navy-500">Total Staff</p>
            <p className="text-2xl font-bold text-navy-900">{staff.length}</p>
          </div>
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              <p className="text-sm text-blue-700">Vendedores</p>
            </div>
            <p className="text-2xl font-bold text-blue-900">{vendedores.length}</p>
          </div>
          <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <p className="text-sm text-purple-700">Otros Roles</p>
            </div>
            <p className="text-2xl font-bold text-purple-900">{admins.length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="vendedor">Vendedores</SelectItem>
                {/* REMOVED: oficial filter option - role eliminated */}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Staff List */}
        <div className="space-y-3">
          <AnimatePresence>
            {filteredStaff.map(s => (
              <StaffRow
                key={s.id}
                staff={s}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
                onChangePassword={(staff) => {
                  setPasswordStaff(staff);
                  setShowPasswordModal(true);
                }}
              />
            ))}
          </AnimatePresence>

          {filteredStaff.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-navy-100">
              <Users className="w-12 h-12 text-navy-300 mx-auto mb-4" />
              <p className="text-navy-500">No se encontró staff</p>
              <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">
                <UserPlus className="w-4 h-4 mr-2" />
                Agregar primer miembro
              </Button>
            </div>
          )}
        </div>

        {/* Create Modal */}
        <CreateStaffModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => fetchStaff()}
        />

        {/* Create Vendor Trial Modal */}
        <CreateVendorTrialModal
          isOpen={showCreateTrialModal}
          onClose={() => setShowCreateTrialModal(false)}
          onCreated={() => fetchStaff()}
        />

        {/* Edit Modal */}
        <EditStaffModal
          isOpen={showEditModal}
          staff={editingStaff}
          onClose={() => {
            setShowEditModal(false);
            setEditingStaff(null);
          }}
          onUpdated={handleEditUpdated}
        />

        {/* Change Password Modal */}
        <ChangePasswordModal
          staff={passwordStaff}
          isOpen={showPasswordModal}
          onClose={() => {
            setShowPasswordModal(false);
            setPasswordStaff(null);
          }}
        />

        {/* Migration Modal */}
        <AnimatePresence>
          {showMigrationModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => !migrating && setShowMigrationModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-navy-900 flex items-center gap-2">
                    <Database className="w-6 h-6 text-amber-600" />
                    Migrar a 6 Pasos
                  </h2>
                  <button 
                    onClick={() => !migrating && setShowMigrationModal(false)}
                    className="p-2 hover:bg-navy-100 rounded-lg"
                    disabled={migrating}
                  >
                    <X className="w-5 h-5 text-navy-500" />
                  </button>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 mb-1">Esta acción:</p>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• Convertirá todos los casos antiguos a 5 pasos</li>
                        <li>• Eliminará los pasos 7, 8 y 9 de cada caso</li>
                        <li>• Recalculará el progreso de cada cliente</li>
                        <li>• Es irreversible</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-emerald-800">
                    <CheckCircle2 className="w-4 h-4 inline mr-2" />
                    Los nuevos casos ya se crean con 5 pasos automáticamente. Esta migración es solo para casos existentes.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowMigrationModal(false)}
                    className="flex-1"
                    disabled={migrating}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleMigrateTo6Steps}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    disabled={migrating}
                  >
                    {migrating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Migrando...
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" />
                        Ejecutar Migración
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default AdminStaff;
